import { clearCart, removeCartItem, updateCart } from "api/cart.service";
import { safeApiCall } from "api/common.service";
import { getStorageKey } from "common/constants";
import { createCookieStorage } from "stores/cookieStorage";
import { Cart, CartItems } from "types/model.types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type CartState = {
  cart: Cart;
  hydrated: boolean;
  setHydrated: () => void;

  setCustomerOrder: (order: string) => void;
  addItem: (item: CartItems[number]) => Promise<void>;
  removeItem: (id: string | number) => Promise<void>;
  clearCart: () => Promise<void>;
  setCart: (cart: Cart) => void;
  updateQuantity: (id: string | number, qty: number) => Promise<void>;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      cart: {
        id: "",
        createdOn: new Date(),
        customerOrder: "",
        items: [],
        selectedProductIds: [],
      },
      hydrated: false,
      setHydrated: () => set({ hydrated: true }),

      setCustomerOrder: (order) =>
        set((state) => ({
          cart: { ...state.cart, customerOrder: order },
        })),

      addItem: async (item) => {
        const currentItems = get().cart.items ?? [];
        const existing = currentItems.find(
          (i) => i.productId === item.productId,
        );

        const updatedItems = existing
          ? currentItems.map((i) =>
              i.productId === item.productId
                ? { ...i, quantity: item.quantity, price: item.price }
                : i,
            )
          : [...currentItems, item];

        set((state) => ({
          cart: { ...state.cart, items: updatedItems },
        }));

        await safeApiCall(
          updateCart(get().cart.id.toString(), item.productId, item.quantity),
        );
      },

      updateQuantity: async (id, qty) => {
        const updatedItems = get().cart.items.map((i) =>
          i.productId === id ? { ...i, quantity: qty } : i,
        );

        set((state) => ({
          cart: { ...state.cart, items: updatedItems },
        }));

        await safeApiCall(
          updateCart(get().cart.id.toString(), id.toString(), qty),
        );
      },

      removeItem: async (id) => {
        const filteredItems = get().cart.items.filter(
          (i) => i.productId !== id,
        );

        set((state) => ({
          cart: { ...state.cart, items: filteredItems },
        }));

        await safeApiCall(removeCartItem(get().cart.id, id.toString()));
      },

      clearCart: async () => {
        set((state) => ({
          cart: { ...state.cart, items: [], customerOrder: "" },
        }));

        await safeApiCall(clearCart(get().cart.id));
      },

      setCart: (cart) => {
        const safeCart: Cart = {
          ...cart,
          items: Array.isArray(cart.items) ? cart.items : [],
          customerOrder: cart.customerOrder ?? "",
        };
        set({ cart: safeCart });
      },
    }),
    {
      name: `${getStorageKey("useCartStore")}`,
      storage: createJSONStorage(() =>
        createCookieStorage({
          path: `/`,
          domain: `${window.location.hostname}`,
          sameSite: "None",
          secure: true,
        }),
      ),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated?.();
      },
    },
  ),
);
